// Where a lead came from (companies.ingestion_source), shown next to the company
// name. Deliberately mirrors the Salesforce mark's shape — a code/icon plus the
// name in a tinted pill in the lead detail, and just the code in the table where
// space is tight — so the two provenance signals read as one system. They answer
// different questions though: the SF mark means "this lead exists in Salesforce"
// (a destination), this one means "this is where the lead came from".
//
// Registered sources get a fixed short code; anything unregistered still renders
// with a derived code, so a new partner list shows up without a frontend change.

interface SourceDef {
  code: string
  label: string
  tint: string
  match: string[]
}

const REGISTRY: SourceDef[] = [
  {
    code: 'VF',
    label: 'Vision Fastigheter',
    tint: 'var(--color-moss)',
    // The name has been spelled several ways across the mail thread, the Pando
    // task and the import flag; all of them should land on the same mark.
    match: ['visionfastigheter', 'visionsfastigheter', 'visionfastigheterab', 'visionsfastigheterab'],
  },
  {
    code: 'MT',
    label: 'Manuellt tillagd',
    tint: 'var(--color-stone)',
    // Leads added by hand in the CRM store ingestion_source "manual".
    match: ['manual'],
  },
]

// `ingestion_source` falls back to this literal when a producer sends no
// platform, so it names no real source — render nothing for it.
const UNSPECIFIED = 'ingest'

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

export function sourceDef(source: string | null | undefined): SourceDef | null {
  const raw = (source ?? '').trim()
  const key = normalize(raw)
  if (!key || key === UNSPECIFIED) return null

  const known = REGISTRY.find(d => d.match.includes(key))
  if (known) return known

  const words = raw.split(/\s+/).filter(Boolean)
  const code =
    words.length > 1
      ? words.slice(0, 2).map(w => w[0]).join('')
      : raw.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2)
  return { code: code.toUpperCase(), label: raw, tint: 'var(--color-stone)', match: [] }
}

const tinted = (tint: string) => ({
  borderColor: `color-mix(in srgb, ${tint} 40%, transparent)`,
  backgroundColor: `color-mix(in srgb, ${tint} 12%, transparent)`,
  color: `color-mix(in srgb, ${tint} 80%, var(--color-ink-1000))`,
})

/** Compact code only — for the table row, next to the company name. */
export function SourceBadge({ source }: { source: string | null | undefined }) {
  const def = sourceDef(source)
  if (!def) return null
  return (
    <span
      className="inline-flex items-center justify-center h-[15px] px-1 rounded border text-[9px] font-bold tracking-wide align-middle shrink-0"
      style={tinted(def.tint)}
      title={`Källa: ${def.label}`}
    >
      {def.code}
    </span>
  )
}

/** Code + full name in a pill — for the lead detail header. */
export default function SourceChip({ source }: { source: string | null | undefined }) {
  const def = sourceDef(source)
  if (!def) return null
  return (
    <span
      className="inline-flex items-center gap-1.5 h-[22px] pl-1.5 pr-2 rounded-full border align-middle"
      style={tinted(def.tint)}
      title={`Källa: ${def.label}`}
    >
      <span className="text-[9px] font-bold tracking-wide">{def.code}</span>
      <span className="text-[11px] font-semibold tracking-wide border-l pl-1.5" style={{ borderColor: 'currentColor', opacity: 0.85 }}>
        {def.label}
      </span>
    </span>
  )
}
